// Modal do nó Conteúdo (mockup "Modal Conteúdo A"): coluna "Adicionar" à
// esquerda + lista de cards (texto/mídia/intervalo) à direita. A lógica de
// sequência (elements/elementsSeq, upload em /flowbuilder/content, leitura
// dos valores via DOM) é a original — só a apresentação mudou.
import React, { useState, useEffect, useRef, useContext } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import IconButton from "@material-ui/core/IconButton";
import Switch from "@material-ui/core/Switch";
import CircularProgress from "@material-ui/core/CircularProgress";
import DragIndicator from "@material-ui/icons/DragIndicator";
import CloseIcon from "@material-ui/icons/Close";
import Layers from "@material-ui/icons/Layers";
import ChatBubbleOutline from "@material-ui/icons/ChatBubbleOutline";
import ImageOutlined from "@material-ui/icons/ImageOutlined";
import Mic from "@material-ui/icons/Mic";
import VideocamOutlined from "@material-ui/icons/VideocamOutlined";
import InsertDriveFileOutlined from "@material-ui/icons/InsertDriveFileOutlined";
import Timer from "@material-ui/icons/Timer";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import { capitalize } from "../../utils/capitalize";
import { AuthContext } from "../../context/Auth/AuthContext";
import AerosDialog from "../AerosDialog";
import useAerosModalStyles from "../../styles/aerosModalStyles";
import useAerosSettingsStyles from "../Settings/aerosSettingsStyles";
import { TONES } from "../../pages/FlowBuilderConfig/flowRules";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const border = isLight ? theme.palette.divider : "rgba(255,255,255,0.08)";
  return {
    grid: { display: "grid", gridTemplateColumns: "150px 1fr", minHeight: 0 },
    left: {
      borderRight: `1px solid ${border}`,
      padding: "14px 12px",
      display: "flex",
      flexDirection: "column",
      gap: 4,
      backgroundColor: isLight ? theme.palette.aeros.surface : theme.palette.aeros.surface2,
    },
    leftLabel: {
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: "0.12em",
      textTransform: "uppercase",
      color: theme.palette.aeros.textTertiary,
      padding: "0 6px 6px",
    },
    addBtn: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "7px 8px",
      borderRadius: 8,
      fontSize: 12,
      fontWeight: 600,
      color: theme.palette.text.secondary,
      background: "none",
      border: "none",
      cursor: "pointer",
      font: "inherit",
      fontSize: 12,
      fontWeight: 600,
      textAlign: "left",
      "& .MuiSvgIcon-root": { fontSize: 16 },
      "&:hover": {
        backgroundColor: isLight ? "rgba(15,20,40,0.05)" : "rgba(255,255,255,0.06)",
        color: theme.palette.text.primary,
      },
    },
    right: {
      padding: "16px 20px",
      display: "flex",
      flexDirection: "column",
      gap: 8,
      overflowY: "auto",
      maxHeight: "60vh",
      scrollBehavior: "smooth",
      ...theme.scrollbarStyles,
    },
    card: {
      display: "flex",
      gap: 10,
      alignItems: "flex-start",
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${border}`,
      borderRadius: 11,
      padding: "10px 12px",
      flexShrink: 0,
    },
    drag: { fontSize: 16, color: theme.palette.aeros.textTertiary, marginTop: 8, flex: "none" },
    tile: {
      width: 30,
      height: 30,
      borderRadius: 8,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flex: "none",
      marginTop: 2,
      "& .MuiSvgIcon-root": { fontSize: 16 },
    },
    kind: {
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: theme.palette.aeros.textTertiary,
      marginBottom: 6,
    },
    remove: {
      padding: 2,
      marginTop: 4,
      color: theme.palette.aeros.textTertiary,
      "& .MuiSvgIcon-root": { fontSize: 16 },
    },
    media: {
      display: "flex",
      flexDirection: "column",
      gap: 8,
      "& img, & video, & embed, & audio": { maxWidth: 220, borderRadius: 8 },
      "& video": { width: 220 },
    },
    fileBtn: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      alignSelf: "flex-start",
      fontSize: 12,
      fontWeight: 600,
      cursor: "pointer",
      padding: "6px 10px",
      borderRadius: 8,
      border: `1px dashed ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.18)"}`,
      color: theme.palette.text.secondary,
      "&:hover": { color: theme.palette.text.primary },
    },
    intervalRow: { display: "flex", alignItems: "center", gap: 8, "& > :first-child": { width: 110 } },
    unit: { fontSize: 12, color: theme.palette.text.secondary },
    dropzone: {
      border: `1px dashed ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.15)"}`,
      borderRadius: 11,
      padding: 12,
      textAlign: "center",
      fontSize: 12,
      color: theme.palette.aeros.textTertiary,
      flexShrink: 0,
    },
    vars: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, flexShrink: 0 },
    varsLabel: { fontSize: 12, fontWeight: 600, color: theme.palette.text.secondary, marginRight: 4 },
    varChip: { backgroundColor: "rgba(160,68,255,0.12)", color: isLight ? "#6D28D9" : "#C08BFF", fontFamily: "ui-monospace, monospace" },
    footerInfo: { fontSize: 12, color: theme.palette.text.secondary, display: "flex", alignItems: "center", gap: 6, "& .MuiSvgIcon-root": { fontSize: 15 } },
    loading: { padding: "48px 24px", display: "flex", flexDirection: "column", alignItems: "center", gap: 14, fontSize: 13, color: theme.palette.text.secondary },
  };
});

const ITEM_META = {
  message: { labelKey: "aeros.flow.content.text", icon: ChatBubbleOutline, tone: TONES.msg },
  img: { labelKey: "aeros.flow.content.image", icon: ImageOutlined, tone: TONES.msg },
  audio: { labelKey: "aeros.flow.content.audio", icon: Mic, tone: TONES.msg },
  video: { labelKey: "aeros.flow.content.video", icon: VideocamOutlined, tone: TONES.msg },
  document: { labelKey: "aeros.flow.content.document", icon: InsertDriveFileOutlined, tone: TONES.msg },
  interval: { labelKey: "aeros.flow.content.interval", icon: Timer, tone: TONES.logic },
};

const FlowBuilderSingleBlockModal = ({ open, onSave, onUpdate, data, close }) => {
  const classes = useStyles();
  const m = useAerosModalStyles();
  const aeros = useAerosSettingsStyles();
  const isMounted = useRef(true);

  const [activeModal, setActiveModal] = useState(false);
  const [medias, setMedias] = useState([]);
  const [elements, setElements] = useState([]);
  const [elementsSeq, setElementsSeq] = useState([]);
  const [elementsSeqEdit, setElementsSeqEdit] = useState([]);
  const [elementsEdit, setElementsEdit] = useState([]);
  const [numberMessages, setNumberMessages] = useState(0);
  const [numberMessagesLast, setNumberMessagesLast] = useState(0);
  const [numberInterval, setNumberInterval] = useState(0);
  const [numberIntervalLast, setNumberIntervalLast] = useState(0);
  const [numberAudio, setNumberAudio] = useState(0);
  const [numberAudioLast, setNumberAudioLast] = useState(0);
  const [numberVideo, setNumberVideo] = useState(0);
  const [numberVideoLast, setNumberVideoLast] = useState(0);
  const [numberImg, setNumberImg] = useState(0);
  const [numberImgLast, setNumberImgLast] = useState(0);
  const [loading, setLoading] = useState(false);
  const [, setPreviewImg] = useState([]);
  const [, setPreviewAudios] = useState([]);
  const [, setPreviewVideos] = useState([]);
  const [variables, setVariables] = useState([]);
  const [, setPreviewDocs] = useState([]);
  const [numberDocLast, setNumberDocLast] = useState(0);
  const [numberDocs, setNumberDocs] = useState(0);
  // segundos de cada intervalo (só para o resumo do rodapé; o valor salvo é lido do DOM)
  const [intervalSecs, setIntervalSecs] = useState({});
  const [labels, setLabels] = useState({ btn: i18n.t("aeros.flow.add") });

  const { user } = useContext(AuthContext);
  const companyId = user.companyId;

  const handleElements = (newNameFiles) => {
    let elementsSequence = [];

    const newArrMessage = elementsSeq.filter((item) => item.includes("message"));
    const newArrInterval = elementsSeq.filter((item) => item.includes("interval"));
    const newArrImg = elementsSeq.filter((item) => item.includes("img"));
    const newArrAudio = elementsSeq.filter((item) => item.includes("audio"));
    const newArrVideo = elementsSeq.filter((item) => item.includes("video"));

    //Todas as mensagens
    for (let i = 0; i < numberMessages; i++) {
      const value = document.querySelector(`.${newArrMessage[i]}`).querySelector(".MuiInputBase-input").value;
      if (!value) {
        toast.error(i18n.t("aeros.flow.content.errors.emptyMessage"));
        setLoading(false);
        throw "";
      }
      elementsSequence.push({ type: "message", value: value, number: newArrMessage[i] });
    }
    //Todos os intervalos
    for (let i = 0; i < numberInterval; i++) {
      const value = document.querySelector(`.${newArrInterval[i]}`).querySelector(".MuiInputBase-input").value;
      if (parseInt(value) === 0 || parseInt(value) > 120) {
        toast.error(i18n.t("aeros.flow.content.errors.intervalRange"));
        setLoading(false);
        throw "";
      }
      elementsSequence.push({ type: "interval", value: value, number: newArrInterval[i] });
    }

    //Todas as imagens
    for (let i = 0; i < numberImg; i++) {
      const onlyImg =
        newNameFiles !== null &&
        newNameFiles.filter((file) => file.includes("png") || file.includes("jpg") || file.includes("jpeg"));
      const onlyImgNameOriginal = medias.filter(
        (file) => file.name.includes("png") || file.name.includes("jpg") || file.name.includes("jpeg")
      );
      if (elementsSeqEdit.includes(newArrImg[i])) {
        const itemSelectedEdit = elementsEdit.filter((item) => item.number === newArrImg[i])[0];
        elementsSequence.push({
          type: "img",
          value: itemSelectedEdit.value,
          original: itemSelectedEdit.original,
          number: itemSelectedEdit.number,
          teste: "image",
        });
      } else {
        let indexElem = 0;
        if (elementsSeqEdit.filter((item) => item.includes("img")).length > 0) {
          indexElem = elementsSeqEdit.filter((item) => item.includes("img")).length - i;
        } else {
          indexElem = i;
        }
        elementsSequence.push({
          type: "img",
          value: onlyImg[indexElem],
          original: onlyImgNameOriginal[indexElem].name,
          number: newArrImg[i],
          teste: "image",
        });
      }
    }
    //Todos os audios
    for (let i = 0; i < numberAudio; i++) {
      const onlyAudio =
        newNameFiles !== null &&
        newNameFiles.filter(
          (file) => file.includes("mp3") || file.includes("ogg") || file.includes("mpeg") || file.includes("opus")
        );
      const onlyAudioNameOriginal = medias.filter(
        (file) =>
          file.name.includes("mp3") || file.name.includes("ogg") || file.name.includes("mpeg") || file.name.includes("opus")
      );
      const record = document.querySelector(`.check${newArrAudio[i]}`).querySelector('input[type="checkbox"]').checked;

      if (elementsSeqEdit.includes(newArrAudio[i])) {
        const itemSelectedEdit = elementsEdit.filter((item) => item.number === newArrAudio[i])[0];
        elementsSequence.push({
          type: "audio",
          value: itemSelectedEdit.value,
          original: itemSelectedEdit.original,
          number: itemSelectedEdit.number,
          record,
        });
      } else {
        let indexElem = 0;
        if (elementsSeqEdit.filter((item) => item.includes("audio")).length > 0) {
          indexElem = elementsSeqEdit.filter((item) => item.includes("audio")).length - i;
        } else {
          indexElem = i;
        }
        elementsSequence.push({
          type: "audio",
          value: onlyAudio[indexElem],
          original: onlyAudioNameOriginal[indexElem].name,
          number: newArrAudio[i],
          record,
        });
      }
    }
    //Todos os videos
    for (let i = 0; i < numberVideo; i++) {
      const onlyVideo =
        newNameFiles !== null && newNameFiles.filter((file) => file.includes("mp4") || file.includes("avi"));
      const onlyVideoNameOriginal = medias.filter((file) => file.name.includes("mp4") || file.name.includes("avi"));
      if (elementsSeqEdit.includes(newArrVideo[i])) {
        const itemSelectedEdit = elementsEdit.filter((item) => item.number === newArrVideo[i])[0];
        elementsSequence.push({
          type: "video",
          value: itemSelectedEdit.value,
          original: itemSelectedEdit.original,
          number: itemSelectedEdit.number,
        });
      } else {
        let indexElem = 0;
        if (elementsSeqEdit.filter((item) => item.includes("video")).length > 0) {
          indexElem = elementsSeqEdit.filter((item) => item.includes("video")).length - i;
        } else {
          indexElem = i;
        }
        elementsSequence.push({
          type: "video",
          value: onlyVideo[indexElem],
          original: onlyVideoNameOriginal[indexElem].name,
          number: newArrVideo[i],
        });
      }
    }
    // Todos os documentos
    for (let i = 0; i < numberDocs; i++) {
      const onlyDocs = newNameFiles !== null && newNameFiles.filter((file) => file.includes("pdf"));
      const onlyDocNameOriginal = medias.filter((file) => file.name.includes("pdf"));

      if (elementsSeqEdit.includes(`document${i}`)) {
        const itemSelectedEdit = elementsEdit.filter((item) => item.number === `document${i}`)[0];
        elementsSequence.push({
          type: "document",
          value: itemSelectedEdit?.value,
          original: itemSelectedEdit?.original,
          number: itemSelectedEdit?.number,
        });
      } else {
        let indexElem = 0;
        if (elementsSeqEdit.filter((item) => item.includes("document")).length > 0) {
          indexElem = elementsSeqEdit.filter((item) => item.includes("document")).length - i;
        } else {
          indexElem = i;
        }
        elementsSequence.push({
          type: "document",
          value: onlyDocs[indexElem],
          original: onlyDocNameOriginal[indexElem].name,
          number: `document${i}`,
        });
      }
    }

    return elementsSequence;
  };

  const removePreview = (setPreview, id) =>
    setPreview((old) => {
      setMedias((oldMedia) => {
        try {
          return oldMedia.filter((mediaItem) => mediaItem.name !== old.filter((item) => item.number === id)[0].name);
        } catch (e) {
          return oldMedia;
        }
      });
      return old.filter((item) => item.number !== id);
    });

  const deleteElementsTypeOne = (id, type) => {
    if (type === "message") setNumberMessages((old) => old - 1);
    if (type === "interval") setNumberInterval((old) => old - 1);
    if (type === "img") {
      setNumberImg((old) => old - 1);
      removePreview(setPreviewImg, id);
    }
    if (type === "audio") {
      setNumberAudio((old) => old - 1);
      removePreview(setPreviewAudios, id);
    }
    if (type === "video") {
      setNumberVideo((old) => old - 1);
      removePreview(setPreviewVideos, id);
    }
    if (type === "document") {
      setNumberDocs((old) => old - 1);
      removePreview(setPreviewDocs, id);
    }
    setElementsSeq((old) => old.filter((item) => item !== `${type}${id}`));
    setElementsSeqEdit((old) => old.filter((item) => item !== `${type}${id}`));
    document.querySelector(`.stack${capitalize(type)}${id}`).remove();
  };

  const tooLarge = (mb) => toast.error(i18n.t("aeros.flow.content.errors.fileTooLarge", { mb }));

  const handleChangeMediasImg = (e, number) => {
    if (!e.target.files) return;
    const arquivo = e.target.files[0];
    if (arquivo.size > 2000000) return tooLarge(2);
    const imgBlob = URL.createObjectURL(arquivo);
    setPreviewImg((old) => [...old, { number: number, url: imgBlob, name: arquivo.name }]);
    setMedias((old) => [...old, arquivo]);
    document.querySelector(`.img${number}`).src = imgBlob;
    document.querySelector(`.btnImg${number}`).remove();
  };

  const handleChangeAudios = (e, number) => {
    if (!e.target.files) return;
    const arquivo = e.target.files[0];
    if (arquivo.size > 5000000) return tooLarge(5);
    const audioBlob = URL.createObjectURL(arquivo);
    setPreviewAudios((old) => [...old, { number: number, url: audioBlob, name: arquivo.name }]);
    setMedias((old) => [...old, arquivo]);
    document.querySelector(`.audio${number}`).innerHTML = `<audio controls="controls">
    <source src="${audioBlob}" type="audio/mp3" />
  </audio>`;
    document.querySelector(`.btnAudio${number}`).remove();
  };

  const handleChangeVideos = (e, number) => {
    if (!e.target.files) return;
    const arquivo = e.target.files[0];
    if (arquivo.size > 20000000) return tooLarge(20);
    const videoBlob = URL.createObjectURL(arquivo);
    setPreviewVideos((old) => [...old, { number: number, url: videoBlob, name: arquivo.name }]);
    setMedias((old) => [...old, arquivo]);
    const divConteudo = document.createElement("div");
    divConteudo.innerHTML = `<video controls="controls" style="width: 220px;">
    <source src="${videoBlob}" type="video/mp4" />
  </video>`;
    document.querySelector(`.video${number}`).appendChild(divConteudo);
    document.querySelector(`.btnVideo${number}`).remove();
  };

  const handleChangeDocuments = (e, number) => {
    if (!e.target.files) return;
    if (e.target.files[0].size > 10000000) return tooLarge(10);
    const file = e.target.files[0];
    const documentBlob = URL.createObjectURL(file);
    setPreviewDocs((old) => [...old, { number: number, url: documentBlob, name: file.name }]);
    setMedias((old) => [...old, file]);
    document.querySelector(`.document${number}`).innerHTML =
      file.type === "application/pdf"
        ? `<embed src="${documentBlob}" type="application/pdf" width="220px" height="200px" />`
        : `<a href="${documentBlob}" target="_blank" rel="noopener noreferrer">${file.name}</a>`;
    document.querySelector(`.btnDocument${number}`).remove();
  };

  // Card no padrão do mockup: drag · tile · rótulo do tipo + controle · X.
  // As classes `stackXxx${n}` são os ganchos do delete via DOM (lógica original).
  const card = (type, number, children) => {
    const meta = ITEM_META[type];
    const Icon = meta.icon;
    const stackClass = `stack${capitalize(type)}${number}`;
    return (
      <div className={`${classes.card} ${stackClass}`} key={stackClass}>
        <DragIndicator className={classes.drag} />
        <div className={classes.tile} style={{ backgroundColor: meta.tone.bg }}>
          <Icon style={{ color: meta.tone.color }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className={classes.kind}>{i18n.t(meta.labelKey)}</div>
          {children}
        </div>
        <IconButton
          className={classes.remove}
          onClick={() => deleteElementsTypeOne(number, type)}
          aria-label={i18n.t("aeros.flow.content.remove")}
        >
          <CloseIcon />
        </IconButton>
      </div>
    );
  };

  const fileButton = (btnClass, accept, onChange) => (
    <label className={`${classes.fileBtn} ${btnClass}`}>
      {i18n.t("aeros.flow.content.chooseFile")}
      <input type="file" accept={accept} hidden onChange={onChange} />
    </label>
  );

  const mediaUrl = (name) => `${process.env.REACT_APP_BACKEND_URL}/public/company${companyId}/flow/${name}`;

  const imgLayout = (number, valueDefault = "") =>
    card(
      "img",
      number,
      <div className={classes.media}>
        <img src={valueDefault.length > 0 ? mediaUrl(valueDefault) : ""} alt="" className={`img${number}`} />
        {valueDefault.length === 0 &&
          fileButton(`btnImg${number}`, "image/png, image/jpg, image/jpeg", (e) => handleChangeMediasImg(e, number))}
      </div>
    );

  const audioLayout = (number, valueDefault = "", valueRecordDefault = "") =>
    card(
      "audio",
      number,
      <div className={classes.media}>
        <div className={`audio${number}`}>
          {valueDefault.length > 0 && (
            <audio controls="controls">
              <source src={mediaUrl(valueDefault)} type="audio/mp3" />
            </audio>
          )}
        </div>
        {valueDefault.length === 0 &&
          fileButton(`btnAudio${number}`, "audio/ogg, audio/mp3, audio/opus", (e) => handleChangeAudios(e, number))}
        <div className={`${m.optionBox} checkaudio${number}`} style={{ padding: "6px 10px" }}>
          <span className={m.optionDesc} style={{ flex: 1 }}>
            {i18n.t("aeros.flow.content.recordedNow")}
          </span>
          <Switch
            color="primary"
            className={aeros.toggleSwitchSmall}
            defaultChecked={valueRecordDefault !== "ok"}
          />
        </div>
      </div>
    );

  const videoLayout = (number, valueDefault = "") =>
    card(
      "video",
      number,
      <div className={classes.media}>
        <div className={`video${number}`}>
          {valueDefault.length > 0 && (
            <video controls="controls">
              <source src={mediaUrl(valueDefault)} type="video/mp4" />
            </video>
          )}
        </div>
        {valueDefault.length === 0 &&
          fileButton(`btnVideo${number}`, "video/mp4", (e) => handleChangeVideos(e, number))}
      </div>
    );

  const documentLayout = (number, valueDefault = "") =>
    card(
      "document",
      number,
      <div className={classes.media}>
        <div className={`document${number}`}>
          {valueDefault.length > 0 && (
            <embed src={mediaUrl(valueDefault)} type="application/pdf" width="220px" height="200px" />
          )}
        </div>
        {valueDefault.length === 0 &&
          fileButton(`btnDocument${number}`, ".doc,.docx,.pdf", (e) => handleChangeDocuments(e, number))}
      </div>
    );

  const messageLayout = (number, valueDefault = "") =>
    card(
      "message",
      number,
      <TextField
        variant="outlined"
        className={`${m.input} message${number}`}
        defaultValue={valueDefault}
        multiline
        minRows={3}
        name="text"
        placeholder={i18n.t("aeros.flow.content.textPlaceholder")}
      />
    );

  const intervalLayout = (number, valueDefault = 0) => {
    setIntervalSecs((old) => ({ ...old, [number]: Number(valueDefault) || 0 }));
    return card(
      "interval",
      number,
      <div className={classes.intervalRow}>
        <TextField
          variant="outlined"
          className={`${m.input} ${m.inputSmall} interval${number}`}
          defaultValue={valueDefault}
          type="number"
          InputProps={{ inputProps: { min: 0, max: 120 } }}
          onChange={(e) => { const segundos = Number(e.target.value) || 0; setIntervalSecs((old) => ({ ...old, [number]: segundos })); }}
        />
        <span className={classes.unit}>{i18n.t("aeros.flow.content.seconds")}</span>
      </div>
    );
  };

  useEffect(() => {
    const localVariables = localStorage.getItem("variables");
    if (localVariables) setVariables(JSON.parse(localVariables));

    if (open === "edit") {
      setLabels({ btn: i18n.t("aeros.flow.save") });
      setElementsSeq(data.data.seq);
      setElementsSeqEdit(data.data.seq);
      setElementsEdit(data.data.elements);
      if (data) {
        const elementsEditLoc = data.data.elements;
        const sequence = data.data.seq;
        const nextNumber = (prefix, setLast) => {
          const arrNumberMax = sequence
            .filter((item) => item.includes(prefix))
            .map((item) => parseInt(item.replace(prefix, "")));
          setLast(Math.max.apply(null, arrNumberMax) + 1);
        };

        sequence.forEach((item) => {
          const itemNode = elementsEditLoc.filter((inode) => inode.number === item)[0];
          if (itemNode.type === "message") {
            setElements((elm) => [...elm, messageLayout(parseInt(item.replace("message", "")), itemNode.value)]);
            setNumberMessages((old) => {
              nextNumber("message", setNumberMessagesLast);
              return old + 1;
            });
          }
          if (itemNode.type === "interval") {
            setElements((elm) => [...elm, intervalLayout(parseInt(item.replace("interval", "")), itemNode.value)]);
            setNumberInterval((old) => {
              nextNumber("interval", setNumberIntervalLast);
              return old + 1;
            });
          }
          if (itemNode.type === "audio") {
            setElements((elm) => [
              ...elm,
              audioLayout(parseInt(item.replace("audio", "")), itemNode.value, itemNode.record ? "" : "ok"),
            ]);
            setNumberAudio((old) => {
              nextNumber("audio", setNumberAudioLast);
              return old + 1;
            });
          }
          if (itemNode.type === "img") {
            setElements((elm) => [...elm, imgLayout(parseInt(item.replace("img", "")), itemNode.value)]);
            setNumberImg((old) => {
              nextNumber("img", setNumberImgLast);
              return old + 1;
            });
          }
          if (itemNode.type === "video") {
            setElements((elm) => [...elm, videoLayout(parseInt(item.replace("video", "")), itemNode.value)]);
            setNumberVideo((old) => {
              nextNumber("video", setNumberVideoLast);
              return old + 1;
            });
          }
          if (itemNode.type === "document") {
            setElements((elm) => [...elm, documentLayout(parseInt(item.replace("document", "")), itemNode.value)]);
            setNumberDocs((old) => {
              nextNumber("document", setNumberDocLast);
              return old + 1;
            });
          }
        });
      }
      setActiveModal(true);
    }
    if (open === "create") {
      setLabels({ btn: i18n.t("aeros.flow.add") });
      setActiveModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleClose = async () => {
    close(null);
    setActiveModal(false);
    setTimeout(() => {
      setMedias([]);
      setPreviewImg([]);
      setPreviewAudios([]);
      setPreviewVideos([]);
      setPreviewDocs([]);
      setElements([]);
      setElementsSeq([]);
      setElementsEdit([]);
      setElementsSeqEdit([]);
      setIntervalSecs({});
      setNumberMessages(0);
      setNumberMessagesLast(0);
      setNumberInterval(0);
      setNumberIntervalLast(0);
      setNumberAudio(0);
      setNumberAudioLast(0);
      setNumberVideo(0);
      setNumberVideoLast(0);
      setNumberImg(0);
      setNumberImgLast(0);
      setNumberDocs(0);
      setNumberDocLast(0);
    }, 500);
  };

  const verifyButtonsUpload = () => {
    const newArrImg = elementsSeq.filter((item) => item.includes("img"));
    const newArrAudio = elementsSeq.filter((item) => item.includes("audio"));
    const newArrVideo = elementsSeq.filter((item) => item.includes("video"));

    for (let i = 0; i < numberImg; i++) {
      if (document.querySelector(`.btn${capitalize(newArrImg[i])}`)) return true;
    }
    for (let i = 0; i < numberAudio; i++) {
      if (document.querySelector(`.btn${capitalize(newArrAudio[i])}`)) return true;
    }
    for (let i = 0; i < numberVideo; i++) {
      if (document.querySelector(`.btn${capitalize(newArrVideo[i])}`)) return true;
    }
    for (let i = 0; i < numberDocs; i++) {
      if (document.querySelector(`.btnDocument${i}`)) return true;
    }
  };

  const handleSaveNode = async () => {
    if (open !== "edit" && open !== "create") return;
    setLoading(true);
    const formData = new FormData();
    medias.forEach((media) => {
      if (!media) return;
      formData.append("typeArch", "flow");
      formData.append("medias", media);
      formData.append("body", media.name);
    });

    const emit = (elementsList) => {
      const mountData = { seq: elementsSeq, elements: elementsList };
      if (open === "edit") onUpdate({ ...data, data: mountData });
      else onSave({ ...mountData });
      toast.success(i18n.t("aeros.flow.content.saved"));
      handleClose();
      setLoading(false);
    };

    setTimeout(async () => {
      const noMedia = numberAudio === 0 && numberVideo === 0 && numberImg === 0 && numberDocs === 0;
      if (noMedia || (open === "edit" && medias.length === 0)) {
        try {
          emit(handleElements(null));
        } catch (e) {
          setLoading(false);
          handleClose();
        }
        return;
      }
      if (verifyButtonsUpload()) {
        setLoading(false);
        return toast.error(i18n.t("aeros.flow.content.errors.emptyCards"));
      }
      await api
        .post("/flowbuilder/content", formData)
        .then((res) => emit(handleElements(res.data)))
        .catch(() => {
          setLoading(false);
          handleClose();
        });
    }, 1500);
  };

  const scrollToBottom = (className) => {
    const element = document.querySelector(className);
    if (element) element.scrollTop = element.scrollHeight;
  };

  const variableFormatter = (item) => "{{" + item + "}}";

  // Mesmo fluxo dos botões originais: cria o layout, incrementa o contador,
  // registra na sequência, avança o "último número" e rola para o fim.
  const addItem = (prefix, layout, last, setCount, setLast) => {
    setElements((old) => [...old, layout(last)]);
    setCount((old) => {
      setElementsSeq((oldEleme) => [...oldEleme, `${prefix}${last}`]);
      return old + 1;
    });
    setLast((old) => old + 1);
    setTimeout(() => scrollToBottom(".body-card"), 100);
  };

  const ADD_BUTTONS = [
    { type: "message", add: () => addItem("message", messageLayout, numberMessagesLast, setNumberMessages, setNumberMessagesLast) },
    { type: "img", add: () => addItem("img", imgLayout, numberImgLast, setNumberImg, setNumberImgLast) },
    { type: "audio", add: () => addItem("audio", audioLayout, numberAudioLast, setNumberAudio, setNumberAudioLast) },
    { type: "video", add: () => addItem("video", videoLayout, numberVideoLast, setNumberVideo, setNumberVideoLast) },
    { type: "document", add: () => addItem("document", documentLayout, numberDocLast, setNumberDocs, setNumberDocLast) },
    { type: "interval", add: () => addItem("interval", intervalLayout, numberIntervalLast, setNumberInterval, setNumberIntervalLast) },
  ];

  const totalSec = elementsSeq.reduce(
    (acc, id) => (id.startsWith("interval") ? acc + (intervalSecs[id.replace("interval", "")] || 0) : acc + 1),
    0
  );

  return (
    <AerosDialog
      open={activeModal}
      onClose={handleClose}
      width={680}
      flushBody
      title={i18n.t(open === "edit" ? "aeros.flow.content.editTitle" : "aeros.flow.content.title")}
      subtitle={i18n.t("aeros.flow.content.subtitle")}
      loading={loading}
      onSubmit={handleSaveNode}
      submitLabel={labels.btn}
      footerLeft={
        <div className={classes.footerInfo}>
          <Layers />
          {i18n.t("aeros.flow.content.footer", { count: elementsSeq.length, sec: totalSec })}
        </div>
      }
    >
      {loading ? (
        <div className={classes.loading}>
          {i18n.t("aeros.flow.content.uploading")}
          <CircularProgress />
        </div>
      ) : (
        <div className={classes.grid}>
          <div className={classes.left}>
            <div className={classes.leftLabel}>{i18n.t("aeros.flow.content.addSection")}</div>
            {ADD_BUTTONS.map(({ type, add }) => {
              const Icon = ITEM_META[type].icon;
              return (
                <button key={type} type="button" className={classes.addBtn} onClick={add}>
                  <Icon style={{ color: ITEM_META[type].tone.color }} />
                  {i18n.t(ITEM_META[type].labelKey)}
                </button>
              );
            })}
          </div>
          <div className={`${classes.right} body-card`}>
            {elements}
            <div className={classes.dropzone}>{i18n.t("aeros.flow.content.dropzone")}</div>
            {variables && variables.length > 0 && (
              <div className={classes.vars}>
                <span className={classes.varsLabel}>{i18n.t("aeros.flow.content.variables")}</span>
                {variables.map((item) => (
                  <span key={item} className={`${m.chip} ${classes.varChip}`}>
                    {variableFormatter(item)}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </AerosDialog>
  );
};

export default FlowBuilderSingleBlockModal;
